import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

import { dashboardApi } from "../../api/dashboardApi";
import { alarmsApi } from "../../api/alarmsApi";
import { frigomatApi, type FrigomatMachine, type FrigomatMachineType, type FrigomatAlarmEntry, getFrigomatConnectionInfo } from "../../api/frigomatApi";
import { serviceReportApi, type ServiceSummaryResponse } from "../../api/serviceReportApi";
import { normalizeTelemetryResponse } from "../../utils/telemetryParser";
import "../../styles/irinox.css";
import "../../styles/rational.css";
import "../../styles/frigomat.css";
import "../../styles/ifi-galileo.css";

import type { DashboardAlarmRow, DashboardTelemetryLatestRow, TelemetryMetricDto } from "../../types/dashboard";
import { useAuth } from "../../auth/AuthContext"; // adatta se diverso

function isoRangeHours(hours: number) {
  const to = new Date();
  const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function getLatest(latestMap: Record<string, DashboardTelemetryLatestRow>, metric: TelemetryMetricDto) {
  // Irinox/Frigomat usano TEMPERATURE_C, Rational usa TEMP_CABINET/TEMP_MIX
  const r = latestMap[metric] ?? latestMap["TEMPERATURE_C"];
  if (!r) return null;
  return `${r.value}${r.unit ? " " + r.unit : ""}`;
}

/** Mappa nome macchina Irinox → immagine dispositivo (abbattitori). Cache-bust con ?v= per forzare refresh. */
const IRINOX_DEVICE_IMAGES: Array<{ pattern: string | RegExp; src: string }> = [
  { pattern: /cucina\s*zanardelli\s*abbattitore\s*2/i, src: "/images/irinox-cucina-zanardelli-abbattitore-2.png" },
  { pattern: /abbattitore\s*1.*zanardelli|zanardelli.*abbattitore\s*1|abbattitore\s*1\s*via\s*zanardelli/i, src: "/images/irinox-abbattitore-1-via-zanardelli.png" },
  { pattern: /abbattitore\s*via\s*abba|abbattitore\s*abba/i, src: "/images/irinox-abbattitore-via-abba.png" },
  { pattern: /via\s*zanardelli\s*gelateria\s*m1|gelateria\s*m1|zanardelli\s*gelateria/i, src: "/images/irinox-via-zanardelli-gelateria-m1.png" },
];

function getIrinoxDeviceImage(name: string): string | null {
  const n = String(name ?? "").trim();
  for (const { pattern, src } of IRINOX_DEVICE_IMAGES) {
    if (typeof pattern === "string" ? n.toLowerCase().includes(pattern.toLowerCase()) : pattern.test(n)) {
      return `${src}?v=2`;
    }
  }
  const def = IRINOX_DEVICE_IMAGES[0]?.src;
  return def ? `${def}?v=2` : null;
}

function metricLabel(m: TelemetryMetricDto) {
  switch (m) {
    case "TEMP_CABINET": return "Temp Cabinet";
    case "TEMP_MIX": return "Temp Mix";
    case "TEMP_EVAP": return "Temp Evaporatore";
    case "TEMP_COND": return "Temp Condensatore";
    case "TEMP_AMBIENT": return "Temp Ambiente";
    case "TEMPERATURE_C": return "Temperatura";
    case "PRESSURE": return "Pressione";
    case "RPM": return "RPM";
    case "POWER": return "Power";
    case "STATE": return "State";
    default: return m;
  }
}

export default function MachineDetailPage() {
  const { provider = "", machineId = "" } = useParams();
  const { token } = useAuth() as any;

  const [loading, setLoading] = useState(true);
  const [machineRow, setMachineRow] = useState<any>(null);

  const [latestMap, setLatestMap] = useState<Record<string, DashboardTelemetryLatestRow>>({});
  const [alarms, setAlarms] = useState<DashboardAlarmRow[]>([]);
  const [localHiddenAlarmIds, setLocalHiddenAlarmIds] = useState<Set<string>>(new Set());

  // Irinox/Frigomat salvano TEMPERATURE_C, Rational TEMP_CABINET
  const [tempMetric, setTempMetric] = useState<TelemetryMetricDto>("TEMPERATURE_C");
  const [powerMetric] = useState<TelemetryMetricDto>("POWER");

  const [tempSeries, setTempSeries] = useState<Array<{ t: string; value: number }>>([]);
  const [powerSeries, setPowerSeries] = useState<Array<{ t: string; value: number }>>([]);
  const [bulkAckLoading, setBulkAckLoading] = useState(false);

  const [irinoxDetail, setIrinoxDetail] = useState<{
    stato: { online: boolean; cycleRunning: number; type: string; domain: string };
    info: Record<string, unknown>;
    alarms: Array<{ name: string; timestamp: number; active: boolean }>;
    status: Record<string, unknown>;
    cycle: Record<string, unknown> | null;
  } | null>(null);
  const [irinoxRecipe, setIrinoxRecipe] = useState<{
    id: string;
    name: string;
    description?: string;
    temperature?: number | null;
  } | null>(null);

  const [rationalDetail, setRationalDetail] = useState<{
    info: Record<string, unknown>;
    stato: Record<string, unknown>;
    temperature: Record<string, unknown>;
    doorStatus: string;
    cleaningMaintenance: Record<string, unknown>;
    program: Record<string, unknown>;
    errors: Array<{ errorCode: string; errorCodeTranslation?: string; active: boolean; startTime?: string }>;
  } | null>(null);
  const [rationalError, setRationalError] = useState<string | null>(null);
  const [rationalErrorsHistory, setRationalErrorsHistory] = useState<
    Array<{
      id: number;
      errorCode: string;
      errorCodeTranslation?: string;
      errorType?: string;
      active: boolean;
      startTime?: string;
      endTime?: string;
      deviceId: number;
    }>
  >([]);
  const [rationalErrorsAccessDenied, setRationalErrorsAccessDenied] = useState(false);

  const [serviceSummary, setServiceSummary] = useState<ServiceSummaryResponse | null>(null);

  const [frigomatDetail, setFrigomatDetail] = useState<FrigomatMachine | null>(null);
  const [frigomatAlarms, setFrigomatAlarms] = useState<FrigomatAlarmEntry[]>([]);
  const [frigomatCatalog, setFrigomatCatalog] = useState<FrigomatMachineType[]>([]);
  const [frigomatRealtimeInfo, setFrigomatRealtimeInfo] = useState<{
    siteId: string | null;
    appToken: string | null | Record<string, unknown>;
    realtimeUrl: string;
  } | null>(null);

  const vendorLabel = useMemo(() => provider, [provider]);
  const isIrinox = useMemo(() => String(provider).toUpperCase() === "IRINOX", [provider]);
  const isRational = useMemo(() => String(provider).toUpperCase() === "RATIONAL", [provider]);
  const isFrigomat = useMemo(() => String(provider).toUpperCase() === "FRIGOMAT", [provider]);
  const isIfiGalileo = useMemo(() => String(provider).toUpperCase() === "IFI_GALILEO", [provider]);

  // 1) carico dashboard e preparo: machine + latest + allarmi macchina
  useEffect(() => {
    setLoading(true);

    dashboardApi
      .getDashboard(token)
      .then((dash) => {
        const md = (dash as any)?.machinesDashboard;
        const items = Array.isArray(md?.items) ? md.items : (Array.isArray(md) ? md : []);
        const row = items.find((m: any) => m.id === machineId) ?? null;
        setMachineRow(row);

        // latest map per macchina
        const latestRows = (dash.telemetryLatest ?? []).filter((x: any) => x.machineId === machineId) as DashboardTelemetryLatestRow[];
        const map: Record<string, DashboardTelemetryLatestRow> = {};
        for (const r of latestRows) map[r.metric] = r;
        setLatestMap(map);

        // allarmi attivi per macchina
        const active = (dash.alarmsActive ?? []).filter((a: any) => a.machineId === machineId) as DashboardAlarmRow[];
        setAlarms(active);

        // Irinox/Frigomat usano TEMPERATURE_C, Rational TEMP_CABINET/TEMP_MIX
        if (map["TEMPERATURE_C"]) setTempMetric("TEMPERATURE_C");
        else if (map["TEMP_CABINET"]) setTempMetric("TEMP_CABINET");
        else if (map["TEMP_MIX"]) setTempMetric("TEMP_MIX");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, machineId]);

  // 1b) carico eventuale service report (es. Rational XML)
  useEffect(() => {
    if (!machineId) {
      setServiceSummary(null);
      return;
    }
    serviceReportApi
      .getServiceSummary(token, machineId)
      .then((res) => setServiceSummary(res))
      .catch(() => setServiceSummary(null));
  }, [token, machineId]);

  // 2) carico serie telemetria per grafici (tollerante al formato)
  useEffect(() => {
    const range = isoRangeHours(24);

    const fetchMetric = async (metric: TelemetryMetricDto) => {
      const res = await dashboardApi.getMachineTelemetry(token, machineId, {
        metric,
        from: range.from,
        to: range.to,
        bucket: "15m",
      });

      // compat: alcuni http wrapper tornano {data: ...}, altri direttamente payload
      const payload = (res as any)?.data ?? res;
      return normalizeTelemetryResponse(payload);
    };

    Promise.all([
      fetchMetric(tempMetric).catch(() => []),
      fetchMetric(powerMetric).catch(() => []),
    ])
      .then(([t, p]) => {
        setTempSeries(t);
        setPowerSeries(p);
      })
      .catch(() => {});
  }, [token, machineId, tempMetric, powerMetric]);

  // 3) Per macchine Irinox: dettaglio stato/info/allarmi da API Irinox
  useEffect(() => {
    if (!isIrinox || !machineId) {
      setIrinoxDetail(null);
      setIrinoxRecipe(null);
      return;
    }
    dashboardApi
      .getIrinoxMachineDetail(token, machineId)
      .then((res) => {
        const payload = (res as any)?.data ?? res;
        setIrinoxDetail({
          stato: payload.stato,
          info: payload.info ?? {},
          alarms: payload.alarms ?? [],
          status: payload.status ?? {},
          cycle: payload.cycle ?? null,
        });
      })
      .catch(() => {
        setIrinoxDetail(null);
        setIrinoxRecipe(null);
      });

    // Esempio: carica dettagli del programma IRINOX_00000301 ("Non stop") per questa macchina
    dashboardApi
      .getIrinoxRecipe(token, machineId, "IRINOX_00000301")
      .then((res) => {
        const r = (res as any)?.data ?? res;
        setIrinoxRecipe({
          id: r.id,
          name: r.name,
          description: r.description,
          temperature: r.temperature ?? null,
        });
      })
      .catch(() => {
        setIrinoxRecipe(null);
      });
  }, [isIrinox, machineId, token]);

  // 4) Per macchine Rational: LIVE da DB (TelemetryLatest, Alarm, AlarmOccurrence) — OPC UA sync + stream
  // REST getRationalMachineDetail è solo enrichment opzionale; se 403 non azzera la UI
  useEffect(() => {
    if (!isRational || !machineId) {
      setRationalDetail(null);
      setRationalError(null);
      setRationalErrorsHistory([]);
      setRationalErrorsAccessDenied(false);
      return;
    }
    setRationalError(null);

    const buildFromLive = (live: {
      telemetryLatest: Array<{ metric: string; value: number; unit: string | null; raw: Record<string, unknown> | null }>;
      alarms: Array<{ id: string; code: string; message: string | null; occurredAt: string }>;
    }) => {
      const tl = live.telemetryLatest;
      const tempC = tl.find((t) => t.metric === "TEMPERATURE_C");
      const coreT = tl.find((t) => t.metric === "OTHER" && (t.raw as any)?.key === "coreTemperature");
      const raw = (tempC?.raw ?? coreT?.raw ?? {}) as Record<string, unknown>;
      const isDoorOpen = raw?.isDoorOpen === true;
      const doorStatus = isDoorOpen ? "Aperta" : "Chiusa";
      const programName = typeof raw?.programName === "string" ? raw.programName : null;
      const remaining = raw?.timeRemainingProgram;
      const remainingStr =
        typeof remaining === "number"
          ? `${remaining} min`
          : remaining != null
          ? String(remaining)
          : "—";

      return {
        info: {
          name: machineRow?.name ?? "—",
          serialNumber: (raw?.serialNumber as string) ?? machineRow?.name ?? "—",
          deviceModel: (raw?.model as string) ?? "—",
          location: "—",
        },
        stato: { deviceStatus: (raw?.operationalState as string) ?? "—" },
        temperature: {
          chamberTemperature: tempC?.value ?? "—",
          foodTemperature: coreT?.value ?? "—",
          humidity: "—",
        },
        doorStatus,
        cleaningMaintenance: { cleaningPercentage: null, maintenancePercentage: null },
        program: { currentProgram: programName ?? "—", remainingTime: remainingStr },
        errors: live.alarms.map((a) => ({
          errorCode: a.code,
          errorCodeTranslation: a.message ?? undefined,
          active: true,
          startTime: a.occurredAt,
        })),
      };
    };

    dashboardApi
      .getMachineLive(token, machineId)
      .then((res) => {
        const payload = (res as any)?.data ?? res;
        const live = {
          telemetryLatest: payload?.telemetryLatest ?? [],
          alarms: payload?.alarms ?? [],
        };
        setRationalDetail(buildFromLive(live));
      })
      .catch(() => {
        setRationalDetail((prev) => {
          if (prev) return prev;
          const fallback = buildFromLive({
            telemetryLatest: Object.entries(latestMap).map(([metric, r]) => ({
              metric,
              value: r.value,
              unit: r.unit ?? null,
              raw: null,
            })),
            alarms: alarms.map((a) => ({ id: a.id, code: a.code ?? a.id, message: a.title ?? null, occurredAt: a.ts })),
          });
          return fallback;
        });
      });

    if (!isRational) return;

    const fetchRationalRealtime = () => {
      dashboardApi.getRationalMachineDetail(token, machineId).then((res) => {
        const payload = (res as any)?.data ?? res;
        setRationalDetail((prev) => ({
          info: payload?.info ?? prev?.info ?? {},
          stato: payload?.stato ?? prev?.stato ?? {},
          temperature: payload?.temperature ?? prev?.temperature ?? {},
          doorStatus: payload?.doorStatus ?? prev?.doorStatus ?? "—",
          cleaningMaintenance: payload?.cleaningMaintenance ?? prev?.cleaningMaintenance ?? {},
          program: payload?.program ?? prev?.program ?? {},
          errors: payload?.errors ?? prev?.errors ?? [],
        }));
        setRationalError(null);
      }).catch((err) => {
        const code = err?.response?.status;
        if (code === 403 || code === 401) {
          setRationalError("REST Connected Cooking non disponibile (accesso limitato). Mostrando dati da OPC UA.");
        } else {
          setRationalError(err?.response?.data?.message ?? err?.message ?? null);
        }
      });

      dashboardApi.getRationalDeviceErrors(token, machineId, 20).then((res) => {
        const payload = (res as any)?.data ?? res;
        setRationalErrorsHistory(Array.isArray(payload?.items) ? payload.items : []);
        setRationalErrorsAccessDenied(Boolean(payload?.accessDenied));
      }).catch(() => {
        setRationalErrorsHistory([]);
        setRationalErrorsAccessDenied(false);
      });
    };

    fetchRationalRealtime();

    const interval = setInterval(fetchRationalRealtime, 90 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- alarms/latestMap used in catch fallback; omit to avoid loops
  }, [isRational, machineId, token, machineRow?.name]);

  // 5) Per macchine Frigomat: dettaglio WiManager + storico allarmi
  const frigomatExternalId = (machineRow as any)?.externalId;
  useEffect(() => {
    if (!isFrigomat || !token) {
      setFrigomatDetail(null);
      setFrigomatAlarms([]);
      setFrigomatCatalog([]);
      return;
    }
    const serial = String(frigomatExternalId ?? "").trim();
    if (!serial) return;

    frigomatApi.getMachineDetail(token, serial)
      .then((d) => setFrigomatDetail(d ?? null))
      .catch(() => setFrigomatDetail(null));

    frigomatApi.getAlarmsHistory(token, serial)
      .then((arr) => setFrigomatAlarms(Array.isArray(arr) ? arr : []))
      .catch(() => setFrigomatAlarms([]));

    frigomatApi.getMachineListCatalog(token)
      .then((c) => setFrigomatCatalog(Array.isArray(c) ? c : []))
      .catch(() => setFrigomatCatalog([]));

    frigomatApi.getRealtimeInfo(token)
      .then((info) => setFrigomatRealtimeInfo(info))
      .catch(() => setFrigomatRealtimeInfo(null));
  }, [isFrigomat, token, frigomatExternalId]);

  const visibleAlarms = alarms.filter((a) => !localHiddenAlarmIds.has(a.id));

  function resolveFrigomatModel(modelId: number | undefined): string {
    if (modelId == null) return "—";
    const m = frigomatCatalog.find((c) => c.id === modelId || c.modelType === modelId);
    return m?.descrizione && m.descrizione !== "-" ? m.descrizione : `Modello ${modelId}`;
  }

  const onAckReset = async (alarmId: string) => {
    // UX: nascondo subito
    setLocalHiddenAlarmIds((prev) => new Set(prev).add(alarmId));

    try {
      await alarmsApi.ackReset(token, alarmId, { note: "ACK/RESET da gestionale", force: false });
      // se vuoi: puoi ricaricare dashboard qui
    } catch (e) {
      // se fallisce, ri-mostro
      setLocalHiddenAlarmIds((prev) => {
        const next = new Set(prev);
        next.delete(alarmId);
        return next;
      });
      alert("ACK/RESET fallito (backend o rete).");
    }
  };

  const onAckResetAllVisible = async () => {
    if (!visibleAlarms.length) return;

    // UX: nascondo subito tutti
    setLocalHiddenAlarmIds((prev) => {
      const next = new Set(prev);
      visibleAlarms.forEach((a) => next.add(a.id));
      return next;
    });

    setBulkAckLoading(true);
    try {
      await Promise.all(
        visibleAlarms.map((a) =>
          alarmsApi.ackReset(token, a.id, {
            note: "ACK/RESET multiplo da dettaglio macchina",
            force: false,
          }),
        ),
      );
    } catch {
      alert("ACK/RESET multiplo fallito per uno o più allarmi.");
      // In caso di errore, ricaricare la pagina è il modo più semplice per riallineare lo stato
    } finally {
      setBulkAckLoading(false);
    }
  };

  return (
    <div className={`container-fluid ${isIfiGalileo ? "ifi-galileo-detail-page" : ""}`}>
      {isIrinox ? (
        <div className="irinox-hero mb-3 d-flex align-items-center justify-content-between">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="irinox-kpi-icon mb-0">
                <i className="bi bi-thermometer-sun" />
              </div>
              <h3 className="mb-0">Dettaglio macchina Irinox</h3>
            </div>
            <div className="small mt-1">
              Vendor: <b>{vendorLabel}</b> • ID: <code>{machineId}</code>
            </div>
          </div>
          <Link to={`/machines/${provider}`} className="btn btn-light btn-sm">
            <i className="bi bi-arrow-left me-1" />
            Torna a {vendorLabel}
          </Link>
        </div>
      ) : isRational ? (
        <div className="rational-hero rational-detail-hero mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <div className="rational-hub-icon" style={{ color: "white", background: "rgba(255,255,255,0.2)" }}>
              <i className="bi bi-cpu-fill" />
            </div>
            <div>
              <h3 className="mb-0 text-white">Dettaglio macchina Rational</h3>
              <div className="small mt-1 opacity-90">
                ID: <code style={{ background: "rgba(255,255,255,0.2)", padding: "0.15rem 0.5rem", borderRadius: 6 }}>{machineId}</code>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Link to={`/machines/${provider}`} className="btn btn-outline-light btn-sm">
              <i className="bi bi-arrow-left me-1" />
              Torna a Rational
            </Link>
          </div>
        </div>
      ) : isFrigomat ? (
        <div className="frigomat-detail-hero mb-3">
          <div className="frigomat-detail-hero-inner">
            <div className="frigomat-detail-hero-brand">
              <div className="frigomat-detail-hero-icon">
                <i className="bi bi-snow2" />
              </div>
              <div>
                <h3 className="frigomat-detail-hero-title">Frigomat WiManager</h3>
                <p className="frigomat-detail-hero-sub">
                  {machineRow?.name ?? machineRow?.machineName ?? "Macchina"}
                  {(machineRow as any)?.externalId && (
                    <span className="frigomat-detail-hero-serial">
                      {" "}· S/N {(machineRow as any).externalId}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <Link to={`/machines/${provider}`} className="btn frigomat-detail-hero-btn">
              <i className="bi bi-arrow-left me-1" />
              Torna a Frigomat
            </Link>
          </div>
        </div>
      ) : isIfiGalileo ? (
        <div className="ifi-detail-hero mb-3">
          <div className="ifi-detail-hero-inner">
            <div className="ifi-detail-hero-brand">
              <div className="ifi-detail-hero-icon">
                <i className="bi bi-gear-wide-connected" />
              </div>
              <div>
                <h1 className="ifi-detail-hero-title">Dettaglio macchina IFI Galileo</h1>
                <p className="ifi-detail-hero-sub">
                  {machineRow?.name ?? machineRow?.machineName ?? "Macchina"}
                  {machineId && (
                    <span className="ifi-detail-hero-id"> · ID {machineId}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Link
                to={`/machines/IFI_GALILEO/${machineId}/analytics`}
                className="btn btn-light btn-sm"
              >
                <i className="bi bi-graph-up-arrow me-1" />
                Analytics
              </Link>
              <Link to={`/machines/${provider}`} className="btn ifi-detail-hero-btn">
                <i className="bi bi-arrow-left me-1" />
                Torna a IFI Galileo
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h3 className="mb-0">Dettaglio macchina</h3>
            <div className="text-muted">
              Vendor: <b>{vendorLabel}</b> • ID: <code>{machineId}</code>
            </div>
          </div>

          <Link to={`/machines/${provider}`} className="btn btn-outline-secondary">
            ← Torna a {vendorLabel}
          </Link>
        </div>
      )}

      {loading && <div className="alert alert-info">Caricamento…</div>}

      {!loading && (
        <>
          {/* KPI in alto: Nome, Stato, Power (Accessa/Spenta) */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6 col-xl-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Nome macchina</div>
                  <div className="fw-bold fs-5">
                    {irinoxDetail?.info?.name
                      ? String(irinoxDetail.info.name)
                      : rationalDetail?.info?.name
                      ? String(rationalDetail.info.name)
                      : machineRow?.name ?? machineRow?.machineName ?? "-"}
                  </div>
                </div>
              </div>
            </div>

            {/* Riassunto ultimo service report (se presente) */}
            {serviceSummary?.report && (
              <div className="col-12 col-md-6 col-xl-4">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div className="text-muted small mb-1">Service report</div>
                    <div className="small">
                      <div><strong>Ultimo report:</strong> {new Date(serviceSummary.report.reportDate).toLocaleString("it-IT")}</div>
                      <div><strong>Seriale:</strong> {serviceSummary.report.common.unitSerialNumber ?? "—"}</div>
                      <div><strong>Modello:</strong> {serviceSummary.report.common.unitType ?? serviceSummary.machine.model ?? "—"}</div>
                      {serviceSummary.report.serviceCounters?.Service_42_2 && (
                        <div className="mt-1">
                          <strong>Service 42.2 (pulizie):</strong>{" "}
                          {serviceSummary.report.serviceCounters.Service_42_2.quantity ?? "0"}{" "}
                          (dal{" "}
                          {serviceSummary.report.serviceCounters.Service_42_2.firstTime
                            ? serviceSummary.report.serviceCounters.Service_42_2.firstTime
                            : "—"}
                          )
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="col-12 col-md-6 col-xl-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Stato</div>
                  <div className="fw-bold fs-5">
                    {(() => {
                      const s =
                        machineRow?.status ??
                        (irinoxDetail?.stato?.online ? "ONLINE" : irinoxDetail ? "OFFLINE" : null) ??
                        (rationalDetail?.stato?.deviceStatus ? String(rationalDetail.stato.deviceStatus) : null);
                      const status = s ?? "UNKNOWN";
                      const cls =
                        status === "ALARM"
                          ? "text-bg-danger"
                          : status === "ONLINE"
                          ? "text-bg-success"
                          : "text-bg-secondary";
                      return <span className={`badge ${cls}`}>{status}</span>;
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Power</div>
                  <div className="fw-bold fs-5">
                    {(() => {
                      const online =
                        machineRow?.status === "ONLINE" ||
                        machineRow?.status === "ALARM" ||
                        machineRow?.isOnline === true ||
                        irinoxDetail?.stato?.online === true;
                      const rationalOnline =
                        rationalDetail?.stato?.deviceStatus &&
                        !["OFFLINE", "NO_CONNECTION", "DISCONNECTED"].some((x) =>
                          String(rationalDetail.stato.deviceStatus).toUpperCase().includes(x)
                        );
                      return online || rationalOnline ? (
                      <span className="text-success">● Accessa</span>
                      ) : (
                        <span className="text-secondary">○ Spenta</span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-4">
              <div className="card shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Temperatura</div>
                  <div className="fw-bold fs-5">
                    {getLatest(latestMap, "TEMPERATURE_C") ??
                      getLatest(latestMap, "TEMP_CABINET") ??
                      getLatest(latestMap, "TEMP_MIX") ??
                      (irinoxDetail?.status?.operativeCoreTemperature != null &&
                      irinoxDetail.status.operativeCoreTemperature !== -1000
                        ? `${irinoxDetail.status.operativeCoreTemperature} °C`
                        : irinoxDetail?.status?.TCell != null && irinoxDetail.status.TCell !== -1000
                        ? `${irinoxDetail.status.TCell} °C`
                        : rationalDetail?.temperature?.chamberTemperature
                        ? String(rationalDetail.temperature.chamberTemperature)
                        : "-")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Blocco Irinox: stato, info, allarmi da API Irinox */}
          {isIrinox && irinoxDetail && (
            <div className="row g-3 mb-3">
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header fw-semibold">Dettaglio Irinox (API)</div>
                  <div className="card-body">
                    {(() => {
                      const machineName = String(irinoxDetail.info?.name ?? machineRow?.name ?? "").trim();
                      const deviceImg = getIrinoxDeviceImage(machineName);
                      return deviceImg ? (
                        <div className="mb-4">
                          <div className="fw-semibold text-muted mb-2 small text-uppercase">Immagine dispositivo</div>
                          <div
                            className="d-flex justify-content-center align-items-center rounded overflow-hidden bg-light"
                            style={{ padding: "1rem", minHeight: 220 }}
                          >
                            <img
                              src={deviceImg}
                              alt={machineName || "Dispositivo Irinox"}
                              className="rounded shadow-sm"
                              style={{
                                maxHeight: 220,
                                objectFit: "contain",
                                transform: "scale(1.08)",
                              }}
                            />
                          </div>
                        </div>
                      ) : null;
                    })()}
                    <div className="row g-3">
                      <div className="col-12 col-md-4">
                        <div className="fw-semibold text-muted mb-2">Stato</div>
                        <ul className="list-unstyled mb-0">
                          <li><strong>Online:</strong> {irinoxDetail.stato.online ? "Sì" : "No"}</li>
                          <li><strong>Ciclo:</strong> {irinoxDetail.stato.cycleRunning ? "In esecuzione" : "Fermo"}</li>
                          <li><strong>Tipo:</strong> {irinoxDetail.stato.type}</li>
                          <li><strong>Domain:</strong> {irinoxDetail.stato.domain}</li>
                        </ul>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="fw-semibold text-muted mb-2">Info macchina</div>
                        <ul className="list-unstyled mb-0 small">
                          <li><strong>Nome:</strong> {String(irinoxDetail.info.name ?? "-")}</li>
                          <li><strong>Seriale:</strong> {String(irinoxDetail.info.serialNumber ?? "-")}</li>
                          <li><strong>Gamma:</strong> {String(irinoxDetail.info.gamma ?? "-")}</li>
                          <li><strong>Modello:</strong> {String(irinoxDetail.info.modelNumber ?? "-")}</li>
                          <li><strong>Lab:</strong> {String(irinoxDetail.info.labName ?? "-")}</li>
                          <li><strong>Azienda:</strong> {String(irinoxDetail.info.companyName ?? "-")}</li>
                          <li><strong>Accessorio:</strong> {String(irinoxDetail.info.accessory ?? "-")}</li>
                        </ul>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="fw-semibold text-muted mb-2">Ciclo / Temperatura</div>
                        {irinoxDetail.cycle && (
                          <ul className="list-unstyled mb-0 small">
                            <li><strong>Ciclo:</strong> {String(irinoxDetail.cycle.runningCycleName ?? "-")}</li>
                            <li><strong>Progresso:</strong> {Number(irinoxDetail.cycle.cycleProgressPercentage ?? 0)}%</li>
                          </ul>
                        )}
                        {irinoxDetail.status && Object.keys(irinoxDetail.status).length > 0 && (
                          <ul className="list-unstyled mb-0 small mt-2">
                            <li><strong>Operativa core:</strong> {String(irinoxDetail.status.operativeCoreTemperature ?? "-")}°C</li>
                            <li><strong>TCell:</strong> {String(irinoxDetail.status.TCell ?? "-")}°C</li>
                            <li><strong>T1/T2/T3 core:</strong> {String(irinoxDetail.status.T1CoreProbe1 ?? "-")} / {String(irinoxDetail.status.T2CoreProbe1 ?? "-")} / {String(irinoxDetail.status.T3CoreProbe1 ?? "-")}°C</li>
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {irinoxRecipe && (
                <div className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header fw-semibold d-flex justify-content-between align-items-center">
                      <span>Programma Irinox (FreshCloud)</span>
                      <span className="badge text-bg-info">
                        {irinoxRecipe.name} ({irinoxRecipe.id})
                      </span>
                    </div>
                    <div className="card-body">
                      {irinoxRecipe.temperature != null && (
                        <div className="mb-2 small text-muted">
                          Temperatura target: <strong>{irinoxRecipe.temperature} °C</strong>
                        </div>
                      )}
                      {irinoxRecipe.description && (
                        <p className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                          {irinoxRecipe.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="col-12">
                <div className="card shadow-sm">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">Lista allarmi Irinox</span>
                    <span className="badge text-bg-secondary">
                      {irinoxDetail.alarms.filter((a) => a.active).length} attivi / {irinoxDetail.alarms.length} totali
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Nome</th>
                          <th>Data/Ora</th>
                          <th>Stato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {irinoxDetail.alarms.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-center text-muted py-3">
                              Nessun allarme registrato
                            </td>
                          </tr>
                        ) : (
                          irinoxDetail.alarms
                            .sort((a, b) => b.timestamp - a.timestamp)
                            .slice(0, 100)
                            .map((a, idx) => (
                              <tr key={`${a.name}-${a.timestamp}-${idx}`} className={a.active ? "table-danger" : ""}>
                                <td className="fw-semibold">{a.name}</td>
                                <td className="text-muted">
                                  {a.timestamp ? new Date(a.timestamp * 1000).toLocaleString("it-IT") : "-"}
                                </td>
                                <td>
                                  <span className={`badge ${a.active ? "text-bg-danger" : "text-bg-secondary"}`}>
                                    {a.active ? "Attivo" : "Risolto"}
                                  </span>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {irinoxDetail.alarms.length > 100 && (
                    <div className="card-footer text-muted small">
                      Mostrati gli ultimi 100 allarmi su {irinoxDetail.alarms.length} totali.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Blocco Rational: dettaglio Connected Cooking */}
          {isRational && rationalError && (
            <div className="row g-3 mb-3">
              <div className="col-12">
                <div className="alert alert-warning">
                  <strong>⚠️ Errore caricamento dati Rational:</strong> {rationalError}
                  <div className="small mt-2">
                    <strong>Possibili cause:</strong>
                    <ul className="mb-0 mt-1">
                      <li>L&apos;<code>externalId</code> nel <code>MachineExternalRef</code> potrebbe essere errato (deve essere il <code>chamberId</code>, non il <code>deviceId</code>)</li>
                      <li>La macchina potrebbe non essere più disponibile su Rational Connected Cooking</li>
                      <li>Problemi di connessione o permessi API</li>
                    </ul>
                    <div className="mt-2">
                      <strong>Nota:</strong> Gli allarmi vengono comunque mostrati se presenti nel database (vedi sezione "Allarmi attivi" sotto).
                    </div>
                    <div className="mt-2">
                      <strong>Soluzione:</strong> Esegui lo script <code>npm run fix:rational-forni</code> nel backend per verificare e correggere automaticamente i mapping.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {isRational && rationalDetail && (
            <div className="rational-detail-section mb-4">
              {/* Hero card con immagine + info */}
              <div className="rational-detail-hero-card card shadow-sm border-0 overflow-hidden mb-4">
                <div className="row g-0">
                  <div className="col-12 col-lg-4 rational-detail-visual">
                    <img
                      src="/images/rational-icombi-pro.png"
                      alt="Rational iCombi Pro"
                      className="rational-detail-img"
                    />
                    <div className="rational-detail-badge">
                      <i className="bi bi-cloud-check me-1" />
                      Connected Cooking
                    </div>
                  </div>
                  <div className="col-12 col-lg-8">
                    <div className="card-body p-4">
                      <h5 className="rational-detail-name mb-3">
                        <i className="bi bi-cpu-fill text-indigo me-2" />
                        {String(rationalDetail.info.name ?? "Macchina Rational")}
                      </h5>
                      <div className="row g-3 mb-0">
                        <div className="col-6 col-md-4">
                          <div className="rational-detail-meta">
                            <span className="rational-detail-meta-label">Seriale</span>
                            <span className="rational-detail-meta-value">{String(rationalDetail.info.serialNumber ?? "—")}</span>
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="rational-detail-meta">
                            <span className="rational-detail-meta-label">Modello</span>
                            <span className="rational-detail-meta-value">
                              {String(rationalDetail.info.deviceModel ?? "—")}
                              {(rationalDetail.info.location ?? (machineRow as any)?.locationLabel ?? (machineRow as any)?.location ?? (machineRow as any)?.address) && (
                                <span className="text-muted small ms-1">
                                  · {String(rationalDetail.info.location ?? (machineRow as any)?.locationLabel ?? (machineRow as any)?.location ?? (machineRow as any)?.address)}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="rational-detail-meta">
                            <span className="rational-detail-meta-label">Posizione</span>
                            <span className="rational-detail-meta-value">{String(rationalDetail.info.location ?? "—")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* KPI live: temperatura, umidità, porta, programma */}
              <div className="row g-3 mb-4">
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="rational-detail-kpi">
                    <i className="bi bi-thermometer-half rational-detail-kpi-icon" />
                    <span className="rational-detail-kpi-label">Temp. camera</span>
                    <span className="rational-detail-kpi-value">{String(rationalDetail.temperature.chamberTemperature ?? "—")}</span>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="rational-detail-kpi">
                    <i className="bi bi-thermometer rational-detail-kpi-icon" />
                    <span className="rational-detail-kpi-label">Temp. cibo</span>
                    <span className="rational-detail-kpi-value">{String(rationalDetail.temperature.foodTemperature ?? "—")}</span>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="rational-detail-kpi">
                    <i className="bi bi-droplet-half rational-detail-kpi-icon" />
                    <span className="rational-detail-kpi-label">Umidità</span>
                    <span className="rational-detail-kpi-value">{String(rationalDetail.temperature.humidity ?? "—")}</span>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className={`rational-detail-kpi ${String(rationalDetail.doorStatus).toLowerCase().includes("aperto") ? "rational-detail-kpi-warning" : ""}`}>
                    <i className="bi bi-door-open rational-detail-kpi-icon" />
                    <span className="rational-detail-kpi-label">Porta</span>
                    <span className="rational-detail-kpi-value">{rationalDetail.doorStatus}</span>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="rational-detail-kpi">
                    <i className="bi bi-gear rational-detail-kpi-icon" />
                    <span className="rational-detail-kpi-label">Programma</span>
                    <span className="rational-detail-kpi-value small text-truncate d-block" title={String(rationalDetail.program.currentProgram ?? "—")}>
                      {String(rationalDetail.program.currentProgram ?? "—")}
                    </span>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2">
                  <div className="rational-detail-kpi">
                    <i className="bi bi-hourglass rational-detail-kpi-icon" />
                    <span className="rational-detail-kpi-label">Rimasto</span>
                    <span className="rational-detail-kpi-value">{String(rationalDetail.program.remainingTime ?? "—")}</span>
                  </div>
                </div>
              </div>

              {/* Barre Cura / Manutenzione */}
              <div className="row g-3 mb-4">
                <div className="col-12 col-md-6">
                  <div className="rational-detail-progress-card">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="rational-detail-progress-label"><i className="bi bi-droplet me-1" />Stato pulizia</span>
                      <span className="rational-detail-progress-pct">
                        {rationalDetail.cleaningMaintenance.cleaningPercentage != null ? `${rationalDetail.cleaningMaintenance.cleaningPercentage}%` : "—"}
                      </span>
                    </div>
                    <div className="progress rational-detail-progress" style={{ height: 8 }}>
                      <div
                        className="progress-bar bg-primary"
                        style={{ width: `${Math.min(100, rationalDetail.cleaningMaintenance.cleaningPercentage ?? 0)}%` }}
                        role="progressbar"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="rational-detail-progress-card">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="rational-detail-progress-label"><i className="bi bi-wrench me-1" />Manutenzione</span>
                      <span className="rational-detail-progress-pct">
                        {rationalDetail.cleaningMaintenance.maintenancePercentage != null ? `${rationalDetail.cleaningMaintenance.maintenancePercentage}%` : "—"}
                      </span>
                    </div>
                    <div className="progress rational-detail-progress" style={{ height: 8 }}>
                      <div
                        className="progress-bar bg-indigo"
                        style={{ width: `${Math.min(100, rationalDetail.cleaningMaintenance.maintenancePercentage ?? 0)}%` }}
                        role="progressbar"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div className="card shadow-sm rational-detail-errors-card">
                  <div className="card-header rational-detail-card-header d-flex align-items-center justify-content-between">
                    <span><i className="bi bi-exclamation-triangle me-2" />Errori dispositivo</span>
                    <span className="badge rational-detail-badge-errors">
                      {rationalDetail.errors.filter((e) => e.active).length} attivi / {rationalDetail.errors.length} totali
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0 align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Descrizione</th>
                          <th>Codice</th>
                          <th>Stato</th>
                          <th>Inizio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rationalDetail.errors.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="text-center text-muted py-3">
                              Nessun errore registrato
                            </td>
                          </tr>
                        ) : (
                          rationalDetail.errors.map((e, idx) => (
                            <tr key={`${e.errorCode}-${idx}`} className={e.active ? "table-danger" : ""}>
                              <td className="fw-semibold">{e.errorCodeTranslation ?? e.errorCode ?? "-"}</td>
                              <td className="text-muted small">{e.errorCode ?? "—"}</td>
                              <td>
                                <span className={`badge ${e.active ? "text-bg-danger" : "text-bg-secondary"}`}>
                                  {e.active ? "Attivo" : "Risolto"}
                                </span>
                              </td>
                              <td className="text-muted">
                                {e.startTime ? new Date(e.startTime).toLocaleString("it-IT") : "-"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {isRational && (
                <div className="col-12">
                  <div className="card shadow-sm rational-detail-errors-card">
                    <div className="card-header rational-detail-card-header d-flex align-items-center justify-content-between">
                      <span><i className="bi bi-clock-history me-2" />Storico errori</span>
                      <span className="badge text-bg-secondary">
                        {rationalErrorsAccessDenied ? "accesso limitato" : `${rationalErrorsHistory.length} eventi`}
                      </span>
                    </div>
                    {rationalErrorsAccessDenied ? (
                      <div className="card-body">
                        <div className="alert alert-warning mb-0">
                          Errori avanzati non disponibili per questo account Rational.
                        </div>
                      </div>
                    ) : rationalErrorsHistory.length === 0 ? (
                      <div className="card-body">
                        <div className="text-muted">Nessun errore disponibile.</div>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Descrizione</th>
                              <th>Codice</th>
                              <th>Attivo</th>
                              <th>Inizio</th>
                              <th>Fine</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rationalErrorsHistory.map((e) => (
                              <tr key={e.id}>
                                <td className="fw-semibold">
                                  {e.errorCodeTranslation ?? e.errorCode ?? "-"}
                                </td>
                                <td className="text-muted small">{e.errorCode ?? "—"}</td>
                                <td>
                                  <span className={`badge ${e.active ? "text-bg-danger" : "text-bg-secondary"}`}>
                                    {e.active ? "Attivo" : "Risolto"}
                                  </span>
                                </td>
                                <td className="text-muted">
                                  {e.startTime ? new Date(e.startTime).toLocaleString("it-IT") : "-"}
                                </td>
                                <td className="text-muted">
                                  {e.endTime ? new Date(e.endTime).toLocaleString("it-IT") : "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Blocco Frigomat: dettaglio WiManager */}
          {isFrigomat && (
            <div className="frigomat-detail-section mb-4">
              <div className="frigomat-detail-card card shadow-sm border-0 overflow-hidden mb-4">
                <div className="frigomat-detail-card-bg" />
                <div className="card-body position-relative">
                  <div className="row g-4 align-items-center">
                    <div className="col-12 col-lg-4 text-center">
                      <div className="frigomat-detail-visual">
                        <i className="bi bi-gear-wide-connected" />
                      </div>
                      <div className="frigomat-detail-badge mt-2">
                        <i className="bi bi-cloud-check me-1" />
                        WiManager
                      </div>
                    </div>
                    <div className="col-12 col-lg-8">
                      <h5 className="frigomat-detail-name mb-3">
                        <i className="bi bi-snow2 text-primary me-2" />
                        {machineRow?.name ?? machineRow?.machineName ?? frigomatDetail?.maC_Matricola ?? "Macchina Frigomat"}
                      </h5>
                      <div className="row g-3">
                        <div className="col-6 col-md-4">
                          <div className="frigomat-detail-meta">
                            <span className="frigomat-detail-meta-label">Seriale MS</span>
                            <span className="frigomat-detail-meta-value">
                              {frigomatDetail?.maC_Numero_Serie_MS ?? (machineRow as any)?.externalId ?? "—"}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="frigomat-detail-meta">
                            <span className="frigomat-detail-meta-label">Matricola</span>
                            <span className="frigomat-detail-meta-value">{frigomatDetail?.maC_Matricola ?? "—"}</span>
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="frigomat-detail-meta">
                            <span className="frigomat-detail-meta-label">Modello</span>
                            <span className="frigomat-detail-meta-value">
                              {resolveFrigomatModel(frigomatDetail?.maC_ModelloSx ?? frigomatDetail?.maC_Macchina)}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="frigomat-detail-meta">
                            <span className="frigomat-detail-meta-label">Firmware</span>
                            <span className="frigomat-detail-meta-value">
                              {frigomatDetail?.maC_VersSwInt ? `v${frigomatDetail.maC_VersSwInt}` : "—"}
                            </span>
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="frigomat-detail-meta">
                            <span className="frigomat-detail-meta-label">Grammi</span>
                            <span className="frigomat-detail-meta-value">{frigomatDetail?.maC_Grammi ?? "—"}</span>
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="frigomat-detail-meta">
                            <span className="frigomat-detail-meta-label">Ultimo aggiornamento</span>
                            <span className="frigomat-detail-meta-value">
                              {frigomatDetail?.maC_LastUpdate
                                ? new Date(frigomatDetail.maC_LastUpdate).toLocaleString("it-IT")
                                : "—"}
                            </span>
                          </div>
                        </div>
                        {(() => {
                          const frigomatRef = (machineRow as any)?.externalRefs?.find(
                            (r: any) => r?.provider === "FRIGOMAT" || r?.account?.provider === "FRIGOMAT",
                          );
                          const conn = getFrigomatConnectionInfo(
                            frigomatDetail ?? null,
                            frigomatRef?.meta?.connectionInfo ?? null,
                          );
                          if (!conn.ipAddress && conn.wifiSignalDb == null && !conn.connectionType) return null;
                          return (
                            <>
                              {conn.ipAddress != null && (
                                <div className="col-6 col-md-4">
                                  <div className="frigomat-detail-meta">
                                    <span className="frigomat-detail-meta-label">
                                      <i className="bi bi-hdd-network me-1" />
                                      Indirizzo IP
                                    </span>
                                    <span className="frigomat-detail-meta-value font-monospace">{conn.ipAddress}</span>
                                  </div>
                                </div>
                              )}
                              {conn.wifiSignalDb != null && (
                                <div className="col-6 col-md-4">
                                  <div className="frigomat-detail-meta">
                                    <span className="frigomat-detail-meta-label">
                                      <i className="bi bi-wifi me-1" />
                                      Segnale WiFi
                                    </span>
                                    <span className="frigomat-detail-meta-value">
                                      {conn.wifiSignalDb} dBm
                                      {conn.wifiSignalDb >= -50 && (
                                        <span className="text-success ms-1" title="Ottimo">●</span>
                                      )}
                                      {conn.wifiSignalDb < -50 && conn.wifiSignalDb >= -70 && (
                                        <span className="text-warning ms-1" title="Buono">●</span>
                                      )}
                                      {conn.wifiSignalDb < -70 && (
                                        <span className="text-danger ms-1" title="Debole">●</span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              )}
                              {conn.connectionType && (
                                <div className="col-6 col-md-4">
                                  <div className="frigomat-detail-meta">
                                    <span className="frigomat-detail-meta-label">Linea / Connessione</span>
                                    <span className="frigomat-detail-meta-value">{conn.connectionType}</span>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vista real-time cloud */}
              {frigomatRealtimeInfo?.realtimeUrl && (
                <div className="card shadow-sm mb-4">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <div>
                        <h6 className="card-title mb-1">
                          <i className="bi bi-broadcast me-2" />
                          Stato in tempo reale (cloud)
                        </h6>
                        <p className="text-muted small mb-0">
                          Verifica lo stato della macchina dal cloud Frigomat WiManager.
                        </p>
                        {frigomatRealtimeInfo.siteId && (
                          <span className="badge bg-secondary mt-1">siteId: {frigomatRealtimeInfo.siteId}</span>
                        )}
                      </div>
                      <a
                        href={frigomatRealtimeInfo.realtimeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                      >
                        <i className="bi bi-box-arrow-up-right me-1" />
                        Apri vista real-time
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Storico allarmi WiManager */}
              <div className="card shadow-sm frigomat-detail-alarms-card">
                <div className="card-header frigomat-detail-card-header">
                  <span><i className="bi bi-bell me-2" />Storico allarmi WiManager</span>
                  <span className="badge frigomat-detail-badge-count">{frigomatAlarms.length} eventi</span>
                </div>
                <div className="frigomat-detail-table-wrap">
                  {frigomatAlarms.length === 0 ? (
                    <div className="card-body text-center py-5 text-muted">
                      <i className="bi bi-check2-circle fs-1 d-block mb-2" />
                      Nessun allarme nello storico
                    </div>
                  ) : (
                    <table className="table table-hover mb-0 align-middle frigomat-detail-table">
                      <thead>
                        <tr>
                          <th>Data / Ora</th>
                          <th>Codice</th>
                          <th>ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {frigomatAlarms.slice(0, 80).map((a, idx) => (
                          <tr key={a.saL_Id ?? idx}>
                            <td>
                              {a.saL_DateTime
                                ? new Date(a.saL_DateTime).toLocaleString("it-IT")
                                : "—"}
                            </td>
                            <td>
                              <span className="badge frigomat-alarm-badge">{a.saL_Allarme ?? "—"}</span>
                            </td>
                            <td className="text-muted">{a.saL_Id ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {frigomatAlarms.length > 80 && (
                  <div className="card-footer text-muted small">
                    Mostrati gli ultimi 80 su {frigomatAlarms.length} totali.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dettaglio completo service report (sensori + storico Service_xx) */}
          {serviceSummary?.report && (
            <div className="card shadow-sm mb-3">
              <div className="card-header fw-semibold">Dettaglio service report</div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12 col-lg-6">
                    <h6 className="text-muted mb-2">Sensori chiave</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-striped align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Sensore</th>
                            <th>Valore</th>
                            <th>Unità</th>
                          </tr>
                        </thead>
                        <tbody>
                          {serviceSummary.report.allSensors &&
                            Object.entries(serviceSummary.report.allSensors).map(([key, val]: [string, any]) => (
                              <tr key={key}>
                                <td className="fw-semibold">{key}</td>
                                <td>{val?.value ?? "-"}</td>
                                <td className="text-muted">{val?.unit ?? ""}</td>
                              </tr>
                            ))}
                          {!serviceSummary.report.allSensors && (
                            <tr>
                              <td colSpan={3} className="text-muted text-center">
                                Nessun dato sensori nel report.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <h6 className="text-muted mb-2">Storico Service_xx</h6>
                    <div className="table-responsive" style={{ maxHeight: 260, overflowY: "auto" }}>
                      <table className="table table-sm table-striped align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Service</th>
                            <th>Qty</th>
                            <th>First</th>
                            <th>Last</th>
                          </tr>
                        </thead>
                        <tbody>
                          {serviceSummary.report.allServiceHistory &&
                            Object.entries(serviceSummary.report.allServiceHistory)
                              .sort(([aKey], [bKey]) => aKey.localeCompare(bKey))
                              .map(([key, val]) => (
                                <tr key={key}>
                                  <td className="fw-semibold">{key}</td>
                                  <td>{val?.quantity ?? ""}</td>
                                  <td className="text-muted">{val?.firstTime ?? ""}</td>
                                  <td className="text-muted">{val?.lastTime ?? ""}</td>
                                </tr>
                              ))}
                          {!serviceSummary.report.allServiceHistory && (
                            <tr>
                              <td colSpan={4} className="text-muted text-center">
                                Nessun dato service nel report.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Selettore metrica temperatura */}
          <div className="card shadow-sm mb-3">
            <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="fw-semibold">Grafici telemetria</div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">Temperatura:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 220 }}
                  value={tempMetric}
                  onChange={(e) => setTempMetric(e.target.value as TelemetryMetricDto)}
                >
                  {(["TEMPERATURE_C","TEMP_CABINET","TEMP_MIX","TEMP_EVAP","TEMP_COND","TEMP_AMBIENT"] as TelemetryMetricDto[]).map((m) => (
                    <option key={m} value={m}>{metricLabel(m)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Grafici */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-lg-7">
              <div className="card shadow-sm h-100">
                <div className="card-header fw-semibold">{metricLabel(tempMetric)} • ultime 24h</div>
                <div className="card-body" style={{ height: 340, minHeight: 200 }}>
                  {tempSeries.length ? (
                    <ResponsiveContainer width="100%" height={340} minHeight={200}>
                      <LineChart data={tempSeries}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="t" hide />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-muted">Nessun dato disponibile per {tempMetric}.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-5">
              <div className="card shadow-sm h-100">
                <div className="card-header fw-semibold">Power • ultime 24h</div>
                <div className="card-body" style={{ height: 340, minHeight: 200 }}>
                  {powerSeries.length ? (
                    <ResponsiveContainer width="100%" height={340} minHeight={200}>
                      <BarChart data={powerSeries}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="t" hide />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-muted">Nessun dato disponibile per POWER.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Allarmi macchina */}
          <div className="card shadow-sm">
            <div className="card-header d-flex align-items-center justify-content-between">
              <div className="fw-semibold">Allarmi attivi</div>
              <div className="d-flex align-items-center gap-2">
                <span className="badge text-bg-danger">{visibleAlarms.length}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  disabled={!visibleAlarms.length || bulkAckLoading}
                  onClick={onAckResetAllVisible}
                >
                  {bulkAckLoading ? "Spengo..." : "Spegni tutti"}
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Quando</th>
                    <th>Severità</th>
                    <th>Titolo</th>
                    <th>Codice</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {visibleAlarms.map((a) => (
                    <tr key={a.id}>
                      <td className="text-muted">{new Date(a.ts).toLocaleString()}</td>
                      <td>
                        <span className={`badge ${
                          a.severity === "CRITICAL" ? "text-bg-danger" :
                          a.severity === "HIGH" ? "text-bg-warning" :
                          "text-bg-secondary"
                        }`}>
                          {a.severity}
                        </span>
                      </td>
                      <td className="fw-semibold">{a.title ?? a.code ?? "Allarme"}</td>
                      <td className="text-muted">{a.code ?? "-"}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => onAckReset(a.id)}
                        >
                          ACK/RESET
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!visibleAlarms.length && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        Nessun allarme attivo
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </>
      )}
    </div>
  );
}